import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

// Api
import { listNotificationsApi } from '../api/endpoints';

// Context
import { useAuth } from './AuthContext';

const DISMISSED_STORAGE_KEY = 'dismissed-notifications';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

const getDismissedFromStorage = () => {
  try {
    const stored = localStorage.getItem(DISMISSED_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveDismissedToStorage = (ids) => {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // localStorage not available
  }
};

export const NotificationProvider = ({ children }) => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => getDismissedFromStorage());
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const data = await listNotificationsApi({ active: true });
      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const dismissNotification = useCallback((id) => {
    setDismissedIds((prev) => {
      if (prev.includes(id)) return prev;
      const updated = [...prev, id];
      saveDismissedToStorage(updated);
      return updated;
    });
  }, []);

  const expandNotification = useCallback((id) => {
    setDismissedIds((prev) => {
      const updated = prev.filter((dismissedId) => dismissedId !== id);
      saveDismissedToStorage(updated);
      return updated;
    });
  }, []);

  const value = {
    notifications,
    dismissedIds,
    loading,
    dismissNotification,
    expandNotification,
    refetchNotifications: fetchNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
